import * as Yup from 'yup';

import {
  calculateEventAmounts,
  calculateEventPercents,
} from 'utils/event-allocation';
import type {
  StockTransferEventAllocation,
  StockTransferEventAllocationRequest,
} from 'store/api/stock-transfers/types';

import { EventSplitRow } from './types';

// T-L09 (owner rule L14, 2026-10-15): "Storage -> Storage: add an event
// selection + split like we have in expenses." Same rows, same rules and the
// same cent-exact helpers as the expense event split
// (utils/event-allocation.ts, shared with components/accounting-expenses-form).

export { calculateEventAmounts, calculateEventPercents };

export const EVENT_SPLIT_FIELD = 'events';

export const eventSplitRowInitialValues: EventSplitRow = {
  amount: undefined,
  eventId: undefined,
  percent: undefined,
};

const cents = (value: unknown) => Math.round(Number(value) * 100);

// StockTransferType.StorageToStorage (a literal here keeps this module free
// of the store's runtime imports).
export const EVENT_SPLIT_TRANSFER_TYPE = 'StorageToStorage';
const CLIENT_TO_CLIENT = 'ClientToClient';

// T-L09 offered the split on Storage -> Storage only. Owner 2026-10-16
// (scope widened): every transfer type but Client -> Client, like expenses;
// saved rows win over the receiving event / the L13 return rule.
export const isEventSplitType = (type?: string | null) =>
  Boolean(type) && type !== CLIENT_TO_CLIENT;

/**
 * The event a transfer's freight goes to when it has no split (the shared
 * rule's default): Storage -> Event and Event -> Event the destination's
 * event, Event -> Storage (L13) the source's; Storage -> Storage none.
 */
export const transferDefaultEventId = ({
  type,
  sourceEventId,
  destinationEventId,
}: {
  type?: string | null;
  sourceEventId?: number | null;
  destinationEventId?: number | null;
}) => {
  const id =
    type === 'StorageToEvent' || type === 'EventToEvent'
      ? destinationEventId
      : type === 'EventToStorage'
        ? sourceEventId
        : null;
  return id ? Number(id) : null;
};

/**
 * True when the rows say nothing more than the default: exactly one row for
 * the default event carrying the whole LTL price. Such rows are never sent,
 * so a record without a real split stays exactly as before.
 */
export const isDefaultOnlySplit = (
  rows: EventSplitRow[] = [],
  defaultEventId?: number | null,
  price?: unknown
) =>
  !rows.length ||
  (Boolean(defaultEventId) &&
    rows.length === 1 &&
    Number(rows[0]?.eventId) === Number(defaultEventId) &&
    cents(rows[0]?.amount) === cents(price));

/** A share field with nothing typed in it. */
export const isBlankShare = (value: unknown) =>
  value === undefined || value === null || value === '';

const blank = isBlankShare;

/** Rows with no percent and no amount yet (events picked, no shares). */
export const allSharesBlank = (rows: EventSplitRow[] = []) =>
  rows.every((row) => blank(row?.amount) && blank(row?.percent));

export const getEventSplitInitialValues = (
  allocations?: StockTransferEventAllocation[] | null
): EventSplitRow[] =>
  (allocations || []).map((row) => ({
    amount: Number(row.amount),
    eventId: Number(row.eventId),
    percent: Number(row.percent),
  }));

/**
 * The split's validation, on top of the transfer form or the LTL dialog.
 * `priceField` names the LTL price field on the same form. No rows = no
 * split (valid; L10: no event).
 */
export const eventSplitSchema = (priceField = 'ltlPrice') =>
  Yup.array()
    .of(
      Yup.object().shape({
        eventId: Yup.number().required('Event is a required field'),
        percent: Yup.number()
          .typeError('Percent must be a number')
          .required('Percent is a required field')
          .min(0)
          .max(100),
        amount: Yup.number()
          .typeError('Amount must be a number')
          .required('Amount is a required field')
          .moreThan(0, 'Amount must be above 0'),
      })
    )
    .test(
      'price-first',
      'Enter the LTL price before splitting it over events',
      function (rows) {
        if (!rows?.length) {
          return true;
        }
        return cents(this.parent?.[priceField]) > 0;
      }
    )
    .test(
      'total-percentage',
      'The total percentage must be 100%',
      function (rows) {
        if (!rows?.length) {
          return true;
        }
        const total = rows.reduce(
          (sum, row) => sum + (Number(row?.percent) || 0),
          0
        );
        return Math.round(total * 100) === 10000;
      }
    )
    .test(
      'total-amount',
      'Event amounts must total the LTL price',
      function (rows) {
        if (
          !rows?.length ||
          rows.some(
            (row) => row?.amount == null || !Number.isFinite(Number(row.amount))
          )
        ) {
          return true;
        }
        return (
          rows.reduce((sum, row) => sum + cents(row.amount), 0) ===
          cents(this.parent?.[priceField])
        );
      }
    );

/** The request rows: dollars with 2 decimals, percent as typed. */
export const buildEventAllocations = (
  rows: EventSplitRow[] = []
): StockTransferEventAllocationRequest[] =>
  rows.map((row) => ({
    eventId: Number(row.eventId),
    percent: String(Number(row.percent)),
    amount: (cents(row.amount) / 100).toFixed(2),
  }));

export const sameEventSplit = (
  left: EventSplitRow[] = [],
  right: EventSplitRow[] = []
) =>
  JSON.stringify(buildEventAllocations(left)) ===
  JSON.stringify(buildEventAllocations(right));

/**
 * Create / edit transfer request body: `events` (the form rows) becomes
 * `eventAllocations` only on a Storage to Storage transfer, and only when
 * there is a split or one was saved before (so it can be cleared). Any other
 * body is exactly what it was before this field existed.
 */
export const withEventSplit = <
  T extends {
    type?: string;
    ltlPrice?: unknown;
    events?: EventSplitRow[];
    eventAllocations?: unknown;
  },
>(
  values: T,
  hadSplit = false,
  defaultEventId: number | null = null
): T => {
  // the saved rows (read with the transfer) are never sent back as they are
  const rest = { ...values };
  delete rest.events;
  delete rest.eventAllocations;
  // the default event at 100% is no split (owner 2026-10-16) while nothing
  // was saved; a saved split is sent as the user left it
  const events =
    !hadSplit &&
    isDefaultOnlySplit(values.events, defaultEventId, values.ltlPrice)
      ? []
      : values.events || [];
  if (isEventSplitType(values.type) && (events.length || hadSplit)) {
    return {
      ...rest,
      eventAllocations: buildEventAllocations(events),
    } as unknown as T;
  }
  return rest as T;
};

/**
 * The rows of a split whose events follow a multi-select (a stock order to a
 * general storage: its Events). Kept rows keep their shares and order; new
 * events get blank rows; removed events lose their row.
 */
export const rowsForEventIds = (
  rows: EventSplitRow[] = [],
  eventIds: number[] = []
): EventSplitRow[] => {
  const wanted = (eventIds || []).map(Number);
  const kept = rows.filter((row) => wanted.includes(Number(row.eventId)));
  const keptIds = kept.map((row) => Number(row.eventId));
  return [
    ...kept,
    ...wanted
      .filter((id) => !keptIds.includes(id))
      .map((eventId) => ({ ...eventSplitRowInitialValues, eventId })),
  ];
};

export const sameEventIdSet = (left: unknown[] = [], right: unknown[] = []) => {
  const unique = (values: unknown[]) =>
    (values || [])
      .map(Number)
      .filter((value, index, all) => all.indexOf(value) === index)
      .sort((x, y) => x - y);
  const a = unique(left);
  const b = unique(right);
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

/** "Disney 80% · $2,156.00 / BMES 20% · $539.00" for lists and details. */
export const eventSplitSummary = (
  allocations?: StockTransferEventAllocation[] | null,
  formatAmount: (value: string) => string = (value) =>
    `$${Number(value).toFixed(2)}`
) =>
  (allocations || [])
    .map(
      (row) =>
        `${row.event?.name || `Event ${row.eventId}`} ${Number(row.percent)}% · ${formatAmount(row.amount)}`
    )
    .join(' / ');

/** "Show A, Show B" for lists and details; '' without a split. */
export const eventSplitNames = (
  allocations?: StockTransferEventAllocation[] | null
) =>
  (allocations || [])
    .map((row) => row.event?.name)
    .filter(Boolean)
    .join(', ');
